import { create } from 'zustand'
import { apiGet, apiPut, apiDelete } from '../services/api'

async function updateAppBadge(count: number) {
  if ('setAppBadge' in navigator) {
    try {
      if (count > 0) {
        await (navigator as any).setAppBadge(count)
      } else {
        await (navigator as any).clearAppBadge()
      }
    } catch {
      // Badge API not supported or permission denied
    }
  }
}

export interface Notification {
  id: number
  title: string
  message: string
  notification_type: 'info' | 'success' | 'warning' | 'error'
  is_read: boolean
  read_at: string | null
  link: string | null
  data: Record<string, unknown> | null
  created_at: string
}

interface NotificationStore {
  notifications: Notification[]
  unreadCount: number
  isLoading: boolean
  error: string | null

  fetchRecentNotifications: (limit?: number) => Promise<void>
  markAsRead: (notificationId: number) => Promise<void>
  markAllAsRead: () => Promise<void>
  deleteNotification: (notificationId: number) => Promise<void>
  clearAll: () => Promise<void>
  refreshUnreadCount: () => Promise<void>
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,

  fetchRecentNotifications: async (limit = 5) => {
    set({ isLoading: true, error: null })
    try {
      const data = await apiGet<{ items: Notification[], unread_count: number }>(
        `/notifications/recent?limit=${limit}`
      )
      updateAppBadge(data.unread_count)
      set({
        notifications: data.items,
        unreadCount: data.unread_count,
        isLoading: false,
      })
    } catch (error) {
      set({ error: 'Failed to fetch notifications', isLoading: false })
    }
  },

  markAsRead: async (notificationId: number) => {
    try {
      await apiPut(`/notifications/${notificationId}/read`)
      const notifications = get().notifications.map(n =>
        n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
      )
      set({ notifications })
      // Refresh real unread count from backend
      await get().refreshUnreadCount()
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  },

  markAllAsRead: async () => {
    try {
      await apiPut('/notifications/read-all')
      const notifications = get().notifications.map(n => ({
        ...n,
        is_read: true,
        read_at: new Date().toISOString(),
      }))
      updateAppBadge(0)
      set({ notifications, unreadCount: 0 })
    } catch (error) {
      console.error('Failed to mark all as read:', error)
    }
  },

  deleteNotification: async (notificationId: number) => {
    try {
      await apiDelete(`/notifications/${notificationId}`)
      const notifications = get().notifications.filter(n => n.id !== notificationId)
      set({ notifications })
      // Refresh real unread count from backend
      await get().refreshUnreadCount()
    } catch (error) {
      console.error('Failed to delete notification:', error)
    }
  },

  clearAll: async () => {
    try {
      await apiDelete('/notifications/clear')
      updateAppBadge(0)
      set({ notifications: [], unreadCount: 0 })
    } catch (error) {
      console.error('Failed to clear notifications:', error)
    }
  },

  refreshUnreadCount: async () => {
    try {
      const data = await apiGet<{ count: number }>('/notifications/unread-count')
      updateAppBadge(data.count)
      set({ unreadCount: data.count })
    } catch (error) {
      console.error('Failed to refresh unread count:', error)
    }
  },
}))
